<script lang="ts" setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useUserStore } from '@/store/user'
import { usePayrollStore } from '@/store/payroll-data'
import { useNotificationsStore } from '@/store/notifications'
import { usePayrollChange } from '@/app/core/composable/routes/useChangePayroll'
import { formatNumber } from '@/app/core/util/numbers'
import { notificationError } from '@/app/core/util/notificationUtils'
import { useI18n } from 'vue-i18n'
import Popper from 'vue3-popper'
import {
  DBtn,
  DSelectFieldSearch,
  DTextField,
  DTable,
  DModal,
  DLoading,
  SearchMdIcon,
  Edit03Icon,
  Trash03Icon
} from 'front-library'
import {
  generateReportList,
  getReportRequest
} from '@/app/core/services/reports'
import {
  getRecurringIncidents,
  RecurringIncident,
  deleteRecurringIncident,
  bulkDeleteRecurringIncidents
} from '@/app/core/services/recurring-incidents'
import {
  getListEmployees,
  getListConcepts,
  getListProcesses
} from '@/app/core/services/incidents'
usePayrollChange(() => window.location.reload())

/* store, router, notifications */
const router = useRouter()
const store = useUserStore()
const notifications = useNotificationsStore()
const { t } = useI18n()

/* Propiedades Computadas */
const filterOptions = computed(() => {
  return (items: any, search: any) =>
    (items = items.filter(
      (item: any) =>
        item.name.toLowerCase().indexOf(search.toString().toLowerCase()) > -1
    ))
})

/* Datos componente Modal y Loading */
const showModal = ref(false)
const titleModal = ref('')
const descriptionModal = ref('')
const typeAlertModal = ref('')

const loading = ref(false)
const messageLoading = ref('')

/* Datos */
const search = ref()
const tableRecurringIncident = ref(null)
const RecurringIncidentForDelete = ref()
const storePayroll = usePayrollStore()
const itemsEmployees = ref([] as any[])
const searchEmployees = ref('')
const employeeId = ref()
const itemsConcepts = ref([] as any[])
const searchConcepts = ref('')
const conceptId = ref()
const itemsProcesses = ref([] as any[])
const searchProcesses = ref('')
const processId = ref()
const searchDateIni = ref()
const searchDateEnd = ref()
const formatter = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 5,
  minimumFractionDigits: 2
})
const urlFile = ref(null)
const refresh = ref()
const payload = ref()
const selectedItems = ref()
const showBulkDeleteModal = ref(false)
const bulkDeleteObservation = ref('')

/* Datos componente table */
const columns = ref([
  { name: 'checkbox', title: '', width: '50px' },
  { name: 'listEmployee', title: t('recurringIncidents.list.employees') },
  { name: 'listProcesses', title: t('recurringIncidents.list.processes') },
  {
    name: 'concept_name',
    title: t('recurringIncidents.list.concept'),
    table: 'concept_name'
  },
  {
    name: 'ri_start_date',
    title: t('recurringIncidents.list.startDate'),
    table: 'ri_start_date'
  },
  {
    name: 'ri_end_date',
    title: t('recurringIncidents.list.endDate'),
    table: 'ri_end_date'
  },
  {
    name: 'ri_quantity',
    title: t('recurringIncidents.list.quantity'),
    table: 'ri_quantity',
    format: (value: any) => formatNumber(value),
    align: 'end'
  },
  {
    name: 'ri_amount',
    title: t('recurringIncidents.list.amount'),
    table: 'amount',
    format: (value: any) => formatNumber(value),
    align: 'end'
  },
  {
    name: 'ri_percentage',
    title: t('recurringIncidents.list.percentage'),
    table: 'percentage'
  },
  {
    name: 'ri_updated_at',
    title: t('recurringIncidents.list.lastUpdate'),
    table: 'ri_updated_at'
  },
  { name: 'actions', title: '' }
])
const rows = ref<RecurringIncident[]>([])
const perPage = ref(0)
const offset = ref(0)
const totalRows = ref(0)
const sortBy = ref('')

/* Redirect's */
const createRecurringIncident = () => {
  void router.push({ name: 'CreateRecurringIncident' })
}
const updateRecurringIncident = (id: any) => {
  void router.push({ name: 'UpdateRecurringIncident', params: { id } })
}
const getRecurringIncident = (id: any) => {
  void router.push({ name: 'GetRecurringIncident', params: { id } })
}

/* Eliminar incidencia recurrente */
const removeRecurringIncident = (values: any) => {
  typeAlertModal.value = 'question'
  titleModal.value = t('recurringIncidents.list.actionStatus')
  descriptionModal.value = t('modal.description')
  showModal.value = true
  RecurringIncidentForDelete.value = values.ri_id
}

/* Eliminar incidencias seleccionadas */
const bulkDelete = () => {
  if (selectedItems?.value?.length === 0) {
    return
  }
  showBulkDeleteModal.value = true
}

const bulkDeleteConfirm = async () => {
  loading.value = true
  messageLoading.value = ''
  showBulkDeleteModal.value = false
  
  try {
    await bulkDeleteRecurringIncidents(selectedItems.value, String(store.user?.name) ,bulkDeleteObservation.value)
    notifications.addNotification({
      title: t('notification.removed'),
      text: t('recurringIncidents.notification.bulkDeleted', {
        count: selectedItems.value.length
      }),
      type: 'success',
      time: 10000
    })
    selectedItems.value = []
    bulkDeleteObservation.value = ''
    void recurringIncident()
  } catch (error: any) {
    notificationError(error, 'recurringIncidents', t, notifications, loading)
  }
}

const closeBulkDeleteModal = () => {
  showBulkDeleteModal.value = false
  bulkDeleteObservation.value = ''
}

const deleteConfirm = async () => {
  loading.value = true
  messageLoading.value = ''
  showModal.value = false
  await deleteRecurringIncident(RecurringIncidentForDelete.value)
    .then(response => {
      notifications.addNotification({
        title: t('notification.removed'),
        text: t('recurringIncidents.notification.action', [
          t('notification.wasRemoved')
        ]),
        type: 'success',
        time: 10000
      })
      void recurringIncident()
    })
    .catch((error: any) => {
      notificationError(error, 'recurringIncidents', t, notifications, loading)
    })
}

/* Generar reporte */
const downloadList = async () => {
  try {
    loading.value = true
    messageLoading.value = t('loading.report')
    urlFile.value = null
    const data = {
      code: 'configurations/entity-table',
      parameters: {
        query: JSON.stringify(payload.value),
        fields: payload.value.fields.filter(
          (field: any) =>
            !field.includes('ri_id') &&
            !field.includes('ri_payroll_id') &&
            !field.includes('currency_symbol')
        ),
        headers: columns.value
          .filter((item: any) => item.title !== '')
          .map((item: any) => item.title),
        module: t('recurringIncidents.list.title', 1)
      }
    }
    const id = await generateReportList(data)
    refresh.value = setInterval(async () => {
      const response = await getReportRequest(id)
      if (response.status === 'Completado') {
        urlFile.value = response.url
        descriptionModal.value = ''
        typeAlertModal.value = 'success'
        titleModal.value = t('reports.notification.reportGenerated')
        showModal.value = true
        loading.value = false
        clearInterval(refresh.value)
      } else if (response.status === 'Error') {
        typeAlertModal.value = 'error'
        titleModal.value = t('reports.notification.reportNoGenerated')
        descriptionModal.value = response.errors
        showModal.value = true
        loading.value = false
        clearInterval(refresh.value)
      }
    }, 5000)
  } catch (error: any) {
    notificationError(error, 'recurringIncidents', t, notifications, loading)
  }
}

/* Cerrar el componente Modal */
const closeModal = () => {
  showModal.value = false
  urlFile.value = null
}

/* Listar incidencias recurrentes */
const recurringIncident = async () => {
  loading.value = true
  messageLoading.value = t('loading.data')
  try {
    const data: any = await getRecurringIncidents(
      offset.value,
      perPage.value,
      sortBy.value,
      search.value === '' ? undefined : search.value,
      employeeId.value,
      conceptId.value,
      searchDateIni.value,
      searchDateEnd.value,
      processId.value
    )
    rows.value = data[0].results
    totalRows.value = data[0].totalItems
    payload.value = data[1]
    loading.value = false
  } catch (error: any) {
    notificationError(error, 'recurringIncidents', t, notifications, loading)
  }
}

onMounted(async () => {
  loading.value = true
  messageLoading.value = t('loading.data')
  try {
    const p1 = new Promise(resolve => {
      resolve(getListEmployees(storePayroll.payroll?.id ?? ''))
    })
    const p2 = new Promise(resolve => {
      resolve(getListConcepts(storePayroll.payroll?.id ?? ''))
    })
    const p3 = new Promise(resolve => {
      resolve(getListProcesses(storePayroll.payroll?.country?.id ?? ''))
    })
    Promise.all([p1, p2, p3])
      .then(async (results: any) => {
        itemsEmployees.value = results[0]
        itemsEmployees.value.unshift({
          id: '',
          name: t('incidents.list.allEmployees')
        })
        itemsConcepts.value = results[1]
        itemsConcepts.value.unshift({
          id: '',
          name: t('incidents.list.allConcepts')
        })
        itemsProcesses.value = results[2]
        itemsProcesses.value.unshift({
          id: '',
          name: t('incidents.list.allProcesses')
        })
        await recurringIncident()
      })
      .catch(async function (error: any) {
        notificationError(error, 'recurringIncidents', t, notifications, loading)
      })
  } catch (error: any) {
    notificationError(error, 'recurringIncidents', t, notifications, loading)
  }
})
</script>
<template>
  <DLoading v-show="loading" :message="messageLoading" />
  <DModal
    :open="showModal"
    :typeAlert="typeAlertModal"
    :title="titleModal"
    :description="descriptionModal"
    :acceptQuestion="
      t('modal.acceptQuestion', {
        action: t('table.action.delete')
      })
    "
    @closeModal="closeModal"
    @otherMethod="deleteConfirm">
    <template v-slot:description v-if="urlFile != null">
      <a
        class="underline tracking-widest text-blue-600"
        :href="urlFile"
        target="_blank"
        v-html="t('reports.notification.viewFile')" />
    </template>
  </DModal>
  <DModal
    :open="showBulkDeleteModal"
    typeAlert="question"
    :title="t('recurringIncidents.list.bulkDeleteTitle', { count: selectedItems?.length })"
    :acceptQuestion="t('modal.acceptQuestion', { action: t('table.action.delete') })"
    @closeModal="closeBulkDeleteModal"
    @otherMethod="bulkDeleteConfirm">
    <template v-slot:description>
      <div class="space-y-3">
        <p>{{ t('recurringIncidents.list.bulkDeleteDescription', { count: selectedItems?.length || 0 }) }}</p>
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">
            {{ t('recurringIncidents.list.bulkDeleteObservation') }}
          </label>
          <textarea
            v-model="bulkDeleteObservation"
            maxlength="300"
            rows="3"
            class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            placeholder="Máximo 300 caracteres"
            required
          />
          <p class="text-xs text-gray-500 mt-1">{{ bulkDeleteObservation.length }}/300 caracteres</p>
        </div>
      </div>
    </template>
  </DModal>
  <div class="h-full px-5 overflow-auto scroll">
    <div class="bg-gray-50 pb-4">
      <div class="leading-4 pt-responsive">
        <p
          class="text-3xl font-semibold text-shadow"
          v-html="t('recurringIncidents.list.title', 1)" />
        <p
          class="text-gray-500 text-shadow"
          v-html="t('recurringIncidents.list.description')" />
      </div>
      <div class="lg:flex justify-between pt-5 gap-4">
        <div class="grid grid-cols-10 md:grid-cols-11 items-end gap-3">
          <DSelectFieldSearch
            class="col-span-10 sm:col-span-5 md:col-span-2"
            :label="t('incidents.list.employee')"
            v-model="employeeId"
            :options="filterOptions(itemsEmployees, searchEmployees)"
            @filter="searchEmployees = $event" />
          <DSelectFieldSearch
            class="col-span-10 sm:col-span-5 md:col-span-2"
            :label="t('incidents.list.concept')"
            v-model="conceptId"
            :options="filterOptions(itemsConcepts, searchConcepts)"
            @filter="searchConcepts = $event" />
          <DSelectFieldSearch
            class="col-span-10 sm:col-span-5 md:col-span-2"
            :label="t('incidents.list.process')"
            v-model="processId"
            :options="filterOptions(itemsProcesses, searchProcesses)"
            @filter="searchProcesses = $event" />
          <DTextField
            class="col-span-10 sm:col-span-5 md:col-span-2"
            type="date"
            :label="t('fields.startDate')"
            v-model="searchDateIni" />
          <DTextField
            class="col-span-10 sm:col-span-5 md:col-span-2"
            type="date"
            :label="t('fields.endDate')"
            v-model="searchDateEnd" />
          <DBtn
            class="h-10 col-span-10 md:col-span-3 lg:col-span-2"
            @click.prevent="recurringIncident()"
            v-html="t('form.button.search')" />
        </div>
        <div class="flex items-end gap-2 pt-3">
          <DBtn
            v-if="store.hasPermission('recurrent_incident:delete')"
            color="danger"
            type="button"
            @click="bulkDelete()">
            {{ t('form.button.delete') }}
          </DBtn>
          <DBtn
            color="excel"
            type="button"
            @click="downloadList"
            v-html="t('form.button.download')" />
          <DBtn
            :icon="'PlusIcon'"
            type="button"
            @click="createRecurringIncident()"
            v-if="store.hasPermission('recurrent_incident:create')">
            {{
              t('form.button.create', {
                form: t('recurringIncidents.list.title', 0)
              })
            }}
          </DBtn>
        </div>
      </div>
    </div>
    <DTable
      ref="tableRecurringIncident"
      :columns="columns"
      :rows="rows"
      :total-rows="totalRows"
      :select-options="true"
      :text-no-data="t('table.noData')"
      :text-per-page="t('table.rowsPerPage')"
      @perPage=";(perPage = $event), recurringIncident()"
      @offset="offset = $event"
      @sort=";(sortBy = $event), recurringIncident()"
      @selected="selectedItems = $event">
      <template #listEmployee="data: any">
        <div class="flex items-center">
          <div
            v-for="(employees, index) in data.employees_name
              .split(',')
              .slice(0, 1)"
            :key="index">
            {{ employees }}
          </div>
          <div
            v-if="data.employees_name.split(',').length > 1"
            class="bg-blue-50 rounded-lg text-blue-700 text-sm font-semibold p-0.5 ml-2 h-6">
            {{ data.employees_name.split(',').length - 1 }}+
          </div>
        </div>
      </template>
      <template #listProcesses="data: any">
        <div class="flex items-center">
          <div
            v-for="(processes, index) in data.processes_name
              .split(',')
              .slice(0, 1)"
            :key="index">
            {{ processes }}
          </div>
          <div
            v-if="data.processes_name.split(',').length > 1"
            class="bg-blue-50 rounded-lg text-blue-700 text-sm font-semibold p-0.5 ml-2 h-6">
            {{ data.processes_name.split(',').length - 1 }}+
          </div>
        </div>
      </template>
      <template #ri_percentage="data: any">
        <div v-if="data.percentage">
          {{
            Number(data.percentage / 100).toLocaleString('en-us', {
              style: 'percent',
              minimumFractionDigits: 0,
              maximumFractionDigits: 5
            })
          }}
        </div>
      </template>
      <template #ri_amount="data: any">
        <div v-if="data.amount">
          {{
            data.currency_symbol === null
              ? ''
              : data.currency_symbol + formatter.format(data.amount)
          }}
        </div>
      </template>
      <template #actions="data: any">
        <div class="flex py-2.5 justify-end gap-1">
          <Popper
            v-if="store.hasPermission('recurrent_incident:get')"
            arrow
            offsetDistance="12"
            :content="t('table.action.detail')"
            :hover="true"
            placement="top"
            class="tooltip">
            <div
              class="bg-gray-50 rounded-md py-2"
              @click="getRecurringIncident(data.ri_id)">
              <SearchMdIcon class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>
          <Popper
            v-if="store.hasPermission('recurrent_incident:update')"
            arrow
            offsetDistance="12"
            :content="t('table.action.edit')"
            :hover="true"
            placement="top"
            class="tooltip">
            <div
              class="bg-gray-50 rounded-md py-2"
              @click="updateRecurringIncident(data.ri_id)">
              <Edit03Icon class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>
          <Popper
            v-if="store.hasPermission('recurrent_incident:delete')"
            arrow
            offsetDistance="12"
            :content="t('table.action.remove')"
            :hover="true"
            placement="top"
            class="tooltip">
            <div
              class="bg-gray-50 rounded-md py-2"
              @click="removeRecurringIncident(data)">
              <Trash03Icon class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>
        </div>
      </template>
    </DTable>
  </div>
</template>
